// src/features/excursions/components/EditExcursionModal.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { Button } from "components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "components/ui/dialog";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "components/ui/select";
import { Checkbox } from "components/ui/checkbox";
import { toast } from "react-hot-toast";
import { formatDateToYYYYMMDD } from "utils/dateUtils";
import { Timestamp } from "firebase/firestore";
import { CalendarCheck } from "lucide-react";
import { checkHoliday } from "services/holidayService";
import { useClients } from "features/clients/hooks/useClients";
import { useGuias } from "features/guias/hooks/useGuias";
import { getOccupiedCamarotes } from "../hooks/useExcursionsData";

const vehicleOptions = [
  "Van",
  "Micro",
  "Ônibus",
  "Ônibus + Micro",
  "2 Ônibus",
  "3 Ônibus",
  "4 Ônibus",
];

const camaroteOptions = ["01", "02", "03", "04"];

const parsePaymentInfo = (value) => {
  if (!value || typeof value !== "string") return "";
  const numericString = value
    .replace(/R\$\s*/, "")
    .replace(/\./g, "")
    .replace(",", ".");
  return parseFloat(numericString) || "";
};

function EditExcursionModal({
  isOpen,
  onClose,
  onUpdateExcursion,
  excursionData,
}) {
  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
    reset,
    watch,
    setValue,
  } = useForm();

  const { addClient, clients, fetchClients } = useClients();
  const { addGuia, guias, fetchGuias } = useGuias();
  const [occupiedCamarotes, setOccupiedCamarotes] = useState([]);
  const [isCheckingCamarotes, setIsCheckingCamarotes] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [filteredClients, setFilteredClients] = useState([]);
  const [showNameSuggestions, setShowNameSuggestions] = useState(false);
  const [showPhoneSuggestions, setShowPhoneSuggestions] = useState(false);

  const [showGuideNameSuggestions, setShowGuideNameSuggestions] = useState(false);
  const [showGuidePhoneSuggestions, setShowGuidePhoneSuggestions] = useState(false);

  const camaroteRequested = watch("camaroteRequested");
  const selectedDateString = watch("date");
  const isGuide = watch("isGuide");
  const organizerName = watch("organizerName");
  const organizerPhone = watch("organizerPhone");
  const clientNameValue = watch("clientName");
  const clientPhoneValue = watch("clientPhone");

  const filteredGuias = useMemo(() => {
    if (!guias || (!showGuideNameSuggestions && !showGuidePhoneSuggestions)) return [];
    
    if (showGuideNameSuggestions) {
      if (!organizerName) return guias.slice(0, 5);
      return guias.filter((g) =>
        g.name.toLowerCase().includes(organizerName.toLowerCase())
      ).slice(0, 5);
    }
    
    if (showGuidePhoneSuggestions) {
      if (!organizerPhone) return guias.slice(0, 5);
      return guias.filter((g) =>
        g.phone.replace(/\D/g, "").includes(organizerPhone.replace(/\D/g, ""))
      ).slice(0, 5);
    }
    
    return [];
  }, [guias, organizerName, organizerPhone, showGuideNameSuggestions, showGuidePhoneSuggestions]);

  const handleSelectGuia = (guia) => {
    setValue("organizerName", guia.name, { shouldValidate: true });
    setValue("organizerPhone", guia.phone, { shouldValidate: true });
    setShowGuideNameSuggestions(false);
    setShowGuidePhoneSuggestions(false);
  };

  useEffect(() => {
    let searchTerm = "";
    let shouldSearch = false;

    if (showNameSuggestions && clientNameValue?.length > 1) {
      searchTerm = clientNameValue;
      shouldSearch = true;
    } else if (showPhoneSuggestions && clientPhoneValue?.length > 1) {
      searchTerm = clientPhoneValue;
      shouldSearch = true;
    }

    if (shouldSearch) {
      const lowerTerm = searchTerm.toLowerCase();
      const matches = clients.filter(
        (c) =>
          c.name.toLowerCase().includes(lowerTerm) ||
          c.phone.includes(lowerTerm) ||
          (c.cpf && c.cpf.includes(lowerTerm)),
      );
      setFilteredClients(matches.slice(0, 5));
    } else {
      setFilteredClients([]);
    }
  }, [
    clientNameValue,
    clientPhoneValue,
    showNameSuggestions,
    showPhoneSuggestions,
    clients,
  ]);

  const handleSelectClient = (client) => {
    setValue("clientName", client.name, { shouldValidate: true });
    setValue("clientPhone", client.phone, { shouldValidate: true });
    setValue("city", client.city || "", { shouldValidate: true });
    setShowNameSuggestions(false);
    setShowPhoneSuggestions(false);
  };

  const SuggestionsList = () => (
    <div className="absolute z-50 w-full mt-1 bg-white dark:bg-accent border border-slate-200 dark:border-border rounded-md shadow-xl max-h-48 overflow-y-auto top-full">
      {filteredClients.map((client) => (
        <div
          key={client.id}
          className="px-4 py-3 cursor-pointer hover:bg-slate-100 dark:hover:bg-accent flex flex-col border-b border-slate-100 dark:border-border last:border-0 transition-colors"
          onClick={() => handleSelectClient(client)}
        >
          <span className="text-sm font-medium text-slate-800 dark:text-foreground">
            {client.name}
          </span>
          <div className="text-xs text-slate-500 dark:text-muted-foreground flex justify-between">
            <span>{client.phone}</span>
            <span>{client.city}</span>
          </div>
        </div>
      ))}
    </div>
  );

  const GuideSuggestionsList = () => (
    <div className="absolute z-50 w-full mt-1 bg-white dark:bg-accent border border-slate-200 dark:border-border rounded-md shadow-xl max-h-48 overflow-y-auto top-full">
      {filteredGuias.map((guia) => (
        <div
          key={guia.id}
          className="px-4 py-3 cursor-pointer hover:bg-slate-100 dark:hover:bg-accent flex flex-col border-b border-slate-100 dark:border-border last:border-0 transition-colors"
          onClick={() => handleSelectGuia(guia)}
        >
          <span className="text-sm font-medium text-slate-800 dark:text-foreground">
            {guia.name}
          </span>
          {guia.phone && (
            <span className="text-xs text-slate-500 dark:text-muted-foreground">
              {guia.phone}
            </span>
          )}
        </div>
      ))}
    </div>
  );

  const holidayName = useMemo(() => {
    if (!selectedDateString) return null;
    const [year, month, day] = selectedDateString.split("-").map(Number);
    if (!year || !month || !day) return null;
    const dateObj = new Date(year, month - 1, day);
    return checkHoliday(dateObj);
  }, [selectedDateString]);

  useEffect(() => {
    if (isOpen) {
      fetchGuias();
      fetchClients();
    }
  }, [isOpen, fetchGuias, fetchClients]);

  useEffect(() => {
    const fetchCamarotes = async () => {
      if (isOpen && selectedDateString) {
        setIsCheckingCamarotes(true);
        const [year, month, day] = selectedDateString.split("-").map(Number);
        const dateObj = new Date(year, month - 1, day);
        const occupied = await getOccupiedCamarotes(dateObj, excursionData?.id);
        setOccupiedCamarotes(occupied);
        setIsCheckingCamarotes(false);
      }
    };
    fetchCamarotes();
  }, [selectedDateString, isOpen, excursionData]);

  useEffect(() => {
    if (excursionData) {
      // Normalização dos Camarotes
      let initialCamaroteNumbers = [];
      if (
        excursionData.camarote?.numbers &&
        Array.isArray(excursionData.camarote.numbers)
      ) {
        // Novo formato (Array)
        initialCamaroteNumbers = excursionData.camarote.numbers;
      } else if (excursionData.camarote?.number) {
        // Formato antigo (String única) - converte para array e formata
        initialCamaroteNumbers = [
          String(excursionData.camarote.number).padStart(2, "0"),
        ];
      }

      reset({
        date: formatDateToYYYYMMDD(excursionData.excursionDate),
        clientName: excursionData.clientName,
        clientPhone: excursionData.clientPhone,
        city: excursionData.city,
        quantity: excursionData.quantity,
        status: excursionData.status,
        vehicle: excursionData.vehicle,
        paymentInfo: parsePaymentInfo(excursionData.paymentInfo),
        camaroteRequested: excursionData.camarote?.requested || false,
        camaroteNumbers: initialCamaroteNumbers, // ✅ Carrega como Array
        comboIncluded: excursionData.comboIncluded || false,
        isGuide: excursionData.isGuide || false,
        organizerName: excursionData.organizerName || "",
        organizerPhone: excursionData.organizerPhone || "",
      });
    }
  }, [excursionData, reset]);

  const onSubmit = async (data) => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      if (!data.clientName || !data.date) {
        toast.error("Data e Nome do Cliente são obrigatórios.");
        return;
      }

      // Validação extra
      if (
        data.camaroteRequested &&
        (!data.camaroteNumbers || data.camaroteNumbers.length === 0)
      ) {
        toast.error("Selecione pelo menos um número de camarote.");
        return;
      }

      await addClient({
        name: data.clientName,
        phone: data.clientPhone,
        city: data.city,
        source: "excursion_edit",
      });

      if (data.isGuide && data.organizerName) {
        await addGuia({
          name: data.organizerName,
          phone: data.organizerPhone || "",
          source: "excursion_edit",
        });
      }

      const excursionDate = new Date(data.date + "T00:00:00");

      await onUpdateExcursion(excursionData.id, {
        excursionDate: Timestamp.fromDate(excursionDate),
        clientName: data.clientName,
        clientPhone: data.clientPhone,
        city: data.city,
        quantity: data.quantity,
        status: data.status,
        vehicle: data.vehicle,
        paymentInfo: data.paymentInfo
          ? `R$ ${new Intl.NumberFormat("pt-BR", {
              minimumFractionDigits: 2,
            }).format(data.paymentInfo)}`
          : "",
        comboIncluded: data.comboIncluded,
        camarote: {
          requested: data.camaroteRequested,
          numbers: data.camaroteNumbers, // ✅ Salva o Array
          number: data.camaroteNumbers?.[0] || "", // Mantém compatibilidade
        },
        isGuide: data.isGuide || false,
        organizerName: data.organizerName || "",
        organizerPhone: data.organizerPhone || "",
      });
      onClose();
    } catch (e) {
      toast.error("Erro ao salvar: " + e.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent aria-describedby={undefined} className="w-full h-[100dvh] max-h-none sm:h-auto sm:max-h-[85vh] sm:max-w-[500px] p-0 border-0 sm:border rounded-none sm:rounded-lg overflow-hidden flex flex-col">
        <DialogHeader className="px-6 py-4 border-b border-slate-100 dark:border-border shrink-0">
          <DialogTitle>Editar Excursão</DialogTitle>
        </DialogHeader>

        <div className="px-6 py-6 overflow-y-auto flex-1">
          <form id="edit-excursion-form" onSubmit={handleSubmit(onSubmit)} className="space-y-8 pb-12">
            {/* -- SEÇÃO: DATA -- */}
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-primary uppercase tracking-wider flex items-center">
                Data
              </h3>
              <div className="flex flex-col w-fit space-y-1">
                <Label htmlFor="date">Data da Excursão</Label>
                <Input
                  id="date"
                  type="date"
                  {...register("date", { required: true })}
                  className="force-light-datepicker w-full min-w-[160px]"
                />
                {holidayName && (
                  <div className="mt-1 flex items-center gap-1 bg-purple-100 text-purple-800 text-xs font-bold px-2 py-1 rounded border border-purple-200 whitespace-nowrap w-full">
                    <CalendarCheck className="h-3 w-3 flex-shrink-0" />
                    {holidayName}
                  </div>
                )}
              </div>
            </div>

            {/* -- SEÇÃO: CLIENTE -- */}
            <div className="space-y-4 pt-2 border-t border-slate-100 dark:border-border">
              <h3 className="text-sm font-semibold text-primary uppercase tracking-wider flex items-center mt-4">
                Cliente
              </h3>
              <div className="space-y-4">
                <div className="space-y-1 relative">
                  <Label htmlFor="clientName">Nome Completo</Label>
                  <Input
                    id="clientName"
                    {...register("clientName", {
                      required: "Nome é obrigatório",
                    })}
                    onFocus={() => {
                      setShowNameSuggestions(true);
                      setShowPhoneSuggestions(false);
                    }}
                    autoComplete="off"
                    placeholder="Nome do cliente..."
                  />
                  {showNameSuggestions && filteredClients.length > 0 && (
                    <SuggestionsList />
                  )}
                  {errors.clientName && (
                    <p className="text-xs text-destructive">
                      {errors.clientName.message}
                    </p>
                  )}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-1 relative">
                    <Label htmlFor="clientPhone">Telefone</Label>
                    <Input
                      id="clientPhone"
                      {...register("clientPhone")}
                      onFocus={() => {
                        setShowPhoneSuggestions(true);
                        setShowNameSuggestions(false);
                      }}
                      autoComplete="off"
                      placeholder="(99) 99999-9999"
                    />
                    {showPhoneSuggestions && filteredClients.length > 0 && (
                      <SuggestionsList />
                    )}
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="city">Cidade/Origem</Label>
                    <Input
                      id="city"
                      {...register("city")}
                      placeholder="Ex: Aracaju"
                    />
                  </div>
                </div>

                {/* -- CHECKBOX DE GUIA INTEGRADO -- */}
                <div className="pt-2 border-t mt-4">
                  <div className="flex items-center space-x-2 mb-3">
                    <Checkbox
                      id="isGuideEdit"
                      checked={isGuide}
                      onCheckedChange={(val) => setValue("isGuide", val)}
                    />
                    <label
                      htmlFor="isGuideEdit"
                      className="text-sm font-medium leading-none cursor-pointer text-green-700 dark:text-muted-foreground"
                    >
                      É uma reserva de Guia de Turismo?
                    </label>
                  </div>

                  {isGuide && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 animate-in fade-in slide-in-from-top-1 bg-green-50/50 dark:bg-card/10 p-3 rounded-md border border-green-100 dark:border-border/30">
                      <div className="space-y-1 relative">
                        <Label htmlFor="organizerNameEdit" className="text-green-900 dark:text-foreground">Nome do Guia</Label>
                        <Input
                          id="organizerNameEdit"
                          {...register("organizerName", { required: isGuide ? "Nome do guia é obrigatório" : false })}
                          onFocus={() => {
                            setShowGuideNameSuggestions(true);
                            setShowGuidePhoneSuggestions(false);
                          }}
                          autoComplete="off"
                          placeholder="Nome do guia..."
                          className="bg-white dark:bg-card"
                        />
                        {showGuideNameSuggestions && filteredGuias.length > 0 && (
                          <GuideSuggestionsList />
                        )}
                        {errors.organizerName && (
                          <p className="text-xs text-destructive">
                            {errors.organizerName.message}
                          </p>
                        )}
                      </div>
                      <div className="space-y-1 relative">
                        <Label htmlFor="organizerPhoneEdit" className="text-green-900 dark:text-foreground">Telefone do Guia</Label>
                        <Input
                          id="organizerPhoneEdit"
                          {...register("organizerPhone")}
                          onFocus={() => {
                            setShowGuidePhoneSuggestions(true);
                            setShowGuideNameSuggestions(false);
                          }}
                          autoComplete="off"
                          placeholder="(99) 99999-9999"
                          className="bg-white dark:bg-card"
                        />
                        {showGuidePhoneSuggestions && filteredGuias.length > 0 && (
                          <GuideSuggestionsList />
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* -- SEÇÃO: DETALHES -- */}
            <div className="space-y-4 pt-2 border-t border-slate-100 dark:border-border">
              <h3 className="text-sm font-semibold text-primary uppercase tracking-wider flex items-center mt-4">
                Detalhes
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label htmlFor="quantity">Quantidade</Label>
                  <Input
                    id="quantity"
                    type="number"
                    {...register("quantity", { valueAsNumber: true })}
                    placeholder="0"
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="vehicle">Veículo</Label>
                  <Controller
                    name="vehicle"
                    control={control}
                    render={({ field }) => (
                      <Select
                        onValueChange={field.onChange}
                        value={field.value}
                        defaultValue=""
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione..." />
                        </SelectTrigger>
                        <SelectContent>
                          {vehicleOptions.map((opt) => (
                            <SelectItem key={opt} value={opt}>
                              {opt}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  />
                </div>
              </div>

              <div className="space-y-1">
                <Label htmlFor="paymentInfo">Pagamento (R$)</Label>
                <Input
                  id="paymentInfo"
                  type="number"
                  step="0.01"
                  {...register("paymentInfo", { valueAsNumber: true })}
                  placeholder="Ex: 150.00"
                />
              </div>

              {/* Opções (Pacote e Camarote) */}
              <div className="space-y-4 pt-2">
                <div className="flex items-center space-x-2 border p-3 rounded-md">
                  <Controller
                    name="comboIncluded"
                    control={control}
                    render={({ field }) => (
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                        id="comboIncluded"
                      />
                    )}
                  />
                  <label
                    htmlFor="comboIncluded"
                    className="text-sm font-medium leading-none cursor-pointer flex-grow"
                  >
                    Combo (Entrada+Almoço)
                  </label>
                </div>

                <div className="border rounded-md p-3 space-y-3">
                  <div className="flex items-center space-x-2">
                    <Controller
                      name="camaroteRequested"
                      control={control}
                      render={({ field }) => (
                        <Checkbox
                          checked={field.value}
                          onCheckedChange={(val) => {
                            field.onChange(val);
                            if (!val) setValue("camaroteNumbers", []); // Limpa ao desmarcar
                          }}
                          id="camaroteRequested"
                        />
                      )}
                    />
                    <label
                      htmlFor="camaroteRequested"
                      className="text-sm font-medium leading-none cursor-pointer flex-grow"
                    >
                      Camarote
                    </label>
                  </div>

                  {/* ✅ SELEÇÃO DE CAMAROTE MÚLTIPLA */}
                  {camaroteRequested && (
                    <div className="animate-in fade-in slide-in-from-top-1 pt-1">
                      <Label className="text-xs text-muted-foreground mb-2 flex justify-between">
                        Selecione (Max 2):
                        {isCheckingCamarotes && (
                          <span className="italic">Verificando...</span>
                        )}
                      </Label>
                      <Controller
                        name="camaroteNumbers" // ✅ Nome do campo no plural
                        control={control}
                        render={({ field }) => (
                          <div className="grid grid-cols-4 gap-2">
                            {camaroteOptions.map((num) => {
                              // Verifica seleção (Array)
                              const currentSelection = field.value || [];
                              const isSelected = currentSelection.includes(num);

                              // Verifica ocupação
                              const isOccupied =
                                occupiedCamarotes.includes(num);

                              return (
                                <button
                                  key={num}
                                  type="button"
                                  disabled={isOccupied}
                                  onClick={() => {
                                    if (isSelected) {
                                      // Remove se já selecionado
                                      field.onChange(
                                        currentSelection.filter(
                                          (n) => n !== num
                                        )
                                      );
                                    } else {
                                      // Adiciona se não selecionado e limite não atingido
                                      if (currentSelection.length < 2) {
                                        field.onChange([
                                          ...currentSelection,
                                          num,
                                        ]);
                                      } else {
                                        toast.error(
                                          "Máximo de 2 camarotes por reserva."
                                        );
                                      }
                                    }
                                  }}
                                  className={`
                                    min-h-[44px] rounded-md text-sm font-medium transition-colors border
                                    ${
                                      isOccupied
                                        ? "bg-muted text-muted-foreground opacity-50 cursor-not-allowed border-transparent decoration-slice line-through"
                                        : isSelected
                                        ? "bg-primary text-primary-foreground border-primary shadow-sm"
                                        : "bg-background hover:bg-accent hover:text-accent-foreground border-input"
                                    }
                                  `}
                                >
                                  {num}
                                </button>
                              );
                            })}
                          </div>
                        )}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>

          </form>
        </div>
        
        <DialogFooter className="px-6 py-4 border-t border-slate-100 dark:border-border bg-slate-50 dark:bg-card shrink-0">
          <Button form="edit-excursion-form" type="submit" size="lg" disabled={isSubmitting} className="w-full sm:w-auto min-h-[44px]">
            {isSubmitting ? "Salvando..." : "Salvar Alterações"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default EditExcursionModal;
